import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, Lock, Mail, User, Phone, LogIn, UserPlus, Check, X } from 'lucide-react';

const AuthPage = ({ setIsAuthenticated }) => {
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('token');
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '', totp_code: '' });
  const [registerData, setRegisterData] = useState({ 
    first_name: '',
    last_name: '',
    date_of_birth: '',
    email: '', 
    phone: '', 
    password: '', 
    confirmPassword: '',
    role: 'parent',
    invitation_token: invitationToken || '',
    // Attorney fields
    bar_license_number: '',
    license_state: '',
    license_expiration_date: '',
    law_firm_name: '',
    // Provider fields
    provider_type: '',
    provider_license_number: '',
    provider_license_state: '',
    organization_name: '',
    // Court Rep fields
    court_name: '',
    jurisdiction: '',
    position: ''
  });
  const [show2FA, setShow2FA] = useState(false);
  const [showVerificationPending, setShowVerificationPending] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [invitationData, setInvitationData] = useState(null);
  const navigate = useNavigate();

  // Validate invitation token on component mount
  useEffect(() => {
    const validateInvitation = async () => {
      if (invitationToken) {
        try {
          const response = await authAPI.acceptInvitation(invitationToken);
          if (response.data.existing_account) {
            toast.info('You already have an account. Please login.');
          } else {
            setInvitationData(response.data.invitation_data);
            setRegisterData(prev => ({
              ...prev,
              email: response.data.invitation_data.email,
              first_name: response.data.invitation_data.name.split(' ')[0] || '',
              last_name: response.data.invitation_data.name.split(' ').slice(1).join(' ') || '',
              full_name: response.data.invitation_data.name,
              invitation_token: invitationToken
            }));
            toast.success(`Welcome! You've been invited by ${response.data.invitation_data.attorney_name}`);
          }
        } catch (error) {
          let errorMsg = 'Invalid or expired invitation';
          if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            if (Array.isArray(detail)) {
              errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
            } else if (typeof detail === 'string') {
              errorMsg = detail;
            }
          }
          toast.error(errorMsg);
        }
      }
    };
    
    validateInvitation();
  }, [invitationToken]);

  // Password validation requirements
  const getPasswordRequirements = (password) => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  };

  const passwordRequirements = getPasswordRequirements(registerData.password);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login({
        email: loginData.email,
        password: loginData.password,
        totp_code: loginData.totp_code || undefined,
      });

      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);

      setIsAuthenticated(true);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      console.error('[LOGIN] Error:', error.response?.status, error.message);

      if (error.response?.data?.detail === '2FA code required') {
        setShow2FA(true);
        toast.error('Please enter your 2FA code');
      } else if (!error.response || error.code === 'ERR_NETWORK' || error.response?.status === 404) {
        // Backend is unreachable (server down, cold-starting, or network issue)
        toast.error('Server is temporarily unavailable. Please wait 30 seconds and try again.', { duration: 8000 });
      } else {
        let errorMsg = 'Login failed';

        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (Array.isArray(detail)) {
            errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          } else if (typeof detail === 'string') {
            errorMsg = detail;
          } else if (typeof detail === 'object') {
            errorMsg = detail.msg || JSON.stringify(detail);
          }
        } else if (error.response?.status === 500) {
          errorMsg = 'Server error. Please try again in a moment.';
        }

        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
      console.log('[LOGIN] Login attempt completed');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (registerData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    // Validate age (must be 18+)
    if (registerData.date_of_birth) {
      const dob = new Date(registerData.date_of_birth);
      const today = new Date();
      const age = Math.floor((today - dob) / (365.25 * 24 * 60 * 60 * 1000));
      
      if (age < 18) {
        toast.error('You must be at least 18 years old to register');
        return;
      }
    }

    setLoading(true);

    try {
      // Build registration payload based on role
      const payload = {
        first_name: registerData.first_name,
        last_name: registerData.last_name,
        date_of_birth: registerData.date_of_birth,
        full_name: `${registerData.first_name} ${registerData.last_name}`.trim(),
        email: registerData.email,
        phone: registerData.phone,
        password: registerData.password,
        role: registerData.role,
        invitation_token: registerData.invitation_token || null
      };

      // Add role-specific information
      if (registerData.role === 'attorney') {
        payload.attorney_license = {
          bar_license_number: registerData.bar_license_number,
          license_state: registerData.license_state,
          license_expiration_date: registerData.license_expiration_date,
          law_firm_name: registerData.law_firm_name || null
        };
      } else if (registerData.role === 'provider') {
        payload.provider_info = {
          provider_type: registerData.provider_type,
          license_number: registerData.provider_license_number || null,
          license_state: registerData.provider_license_state || null,
          organization_name: registerData.organization_name || null
        };
      } else if (registerData.role === 'court_rep') {
        payload.court_rep_info = {
          court_name: registerData.court_name,
          jurisdiction: registerData.jurisdiction,
          position: registerData.position
        };
      }

      const response = await authAPI.register(payload);

      setPendingEmail(registerData.email);
      setShowVerificationPending(true);
      toast.success('Registration successful! Please check your email to verify your account.');
      setRegisterData({ first_name: '', last_name: '', date_of_birth: '', email: '', phone: '', password: '', confirmPassword: '', role: 'parent' });
    } catch (error) {
      let errorMsg = 'Registration failed';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle Pydantic validation errors (array format)
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      await authAPI.resendVerification(pendingEmail);
      toast.success('Verification email resent! Please check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    }
  };

  // Password Requirements Component
  const PasswordRequirement = ({ met, text }) => (
    <div className="flex items-center space-x-2 text-sm">
      {met ? (
        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
      ) : (
        <X className="w-4 h-4 text-slate-400 flex-shrink-0" />
      )}
      <span className={met ? 'text-emerald-600 font-medium' : 'text-slate-500'}>
        {text}
      </span>
    </div>
  );

  if (showVerificationPending) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700">
          <CardContent className="pt-12 pb-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-indigo-500/20 flex items-center justify-center">
                  <Mail className="w-10 h-10 text-indigo-400" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">Check Your Email</h2>
              <p className="text-slate-400 mb-6">
                We've sent a verification link to <strong className="text-slate-300">{pendingEmail}</strong>
              </p>
              <p className="text-sm text-slate-500 mb-8">
                Click the link in the email to verify your account and complete registration.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={handleResendVerification}
                  variant="outline"
                  className="w-full border-slate-600 hover:bg-slate-700"
                >
                  Resend Verification Email
                </Button>
                <Button
                  onClick={() => setShowVerificationPending(false)}
                  variant="ghost"
                  className="w-full text-slate-400 hover:text-slate-300"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="w-full max-w-4xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left side - Branding */}
        <div className="text-white space-y-6 animate-fade-in">
          <div className="flex items-center space-x-3">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>FamilyTrails</h1>
          </div>
          <p className="text-xl text-white/90">Building better co-parenting journeys together</p>
          <ul className="space-y-4">
            <li className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">Bank-Level Security</p>
                <p className="text-sm text-white/80">2FA and encrypted communication</p>
              </div>
            </li>
            <li className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">Court-Admissible Records</p>
                <p className="text-sm text-white/80">All communications documented</p>
              </div>
            </li>
            <li className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">AI Tone Monitoring</p>
                <p className="text-sm text-white/80">Promotes positive communication</p>
              </div>
            </li>
          </ul>
        </div>

        {/* Right side - Auth forms */}
        <Card className="animate-fade-in shadow-2xl bg-white" style={{ minHeight: 'auto', maxHeight: '90vh' }}>
          <CardHeader className="pb-4">
            <CardTitle className="text-2xl">Welcome to FamilyTrails</CardTitle>
            <CardDescription>
              {invitationToken ? (
                invitationData ? (
                  `Complete your registration - invited by ${invitationData.attorney_name}`
                ) : (
                  'Validating your invitation...'
                )
              ) : (
                'Sign in to your account'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 100px)' }}>
            <Tabs defaultValue={invitationToken ? "register" : "login"} className="w-full">
              {!invitationToken && (
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                  <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Professional</TabsTrigger>
                </TabsList>
              )}

              <TabsContent value="login" className="mt-6">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-slate-700">Email Address</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="h-11"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password" className="text-slate-700">Password</Label>
                      <button
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
                      >
                        Forgot Password?
                      </button>
                    </div>
                    <Input
                      id="login-password"
                      data-testid="login-password"
                      type="password"
                      placeholder="Enter your password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="h-11"
                      required
                    />
                  </div>
                  {show2FA && (
                    <div className="space-y-2">
                      <Label htmlFor="login-2fa" className="text-slate-700">2FA Code</Label>
                      <Input
                        id="login-2fa"
                        data-testid="login-2fa"
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={loginData.totp_code}
                        onChange={(e) => setLoginData({ ...loginData, totp_code: e.target.value })}
                        className="h-11"
                        maxLength={6}
                      />
                    </div>
                  )}
                  <div className="pt-6">
                    <button
                      data-testid="login-button"
                      type="submit"
                      disabled={loading}
                      className="w-full h-14 px-4 py-3 text-lg font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                          <span>Signing in...</span>
                        </>
                      ) : (
                        <>
                          <LogIn className="w-5 h-5" />
                          <span>Login to Account</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </TabsContent>

              {/* Parent Sign Up Tab - Simple registration for parents */}
              <TabsContent value="signup" className="mt-6">
                {!invitationToken ? (
                  <div className="space-y-6 py-4">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <Shield className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h3 className="font-semibold text-amber-900 text-base">Invitation Required</h3>
                          <p className="text-sm text-amber-800 mt-1">
                            Parent registration requires an invitation link from your attorney. Please contact your attorney or legal representative to receive a registration link.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="text-center text-sm text-slate-500">
                      <p>Already have an account? Switch to the <button type="button" onClick={() => document.querySelector('[data-testid="login-tab"]')?.click()} className="text-indigo-600 hover:text-indigo-700 font-medium">Login</button> tab.</p>
                      <p className="mt-2">Are you a professional? <button type="button" onClick={() => document.querySelector('[data-testid="register-tab"]')?.click()} className="text-indigo-600 hover:text-indigo-700 font-medium">Register as Attorney, Provider, or Court Rep</button></p>
                    </div>
                  </div>
                ) : (
                <form onSubmit={handleRegister} className="space-y-4">
                  <input type="hidden" value="parent" />
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="signup-first-name" className="text-slate-700">First Name *</Label>
                      <Input
                        id="signup-first-name"
                        data-testid="signup-first-name"
                        type="text"
                        placeholder="John"
                        value={registerData.first_name}
                        onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value, role: 'parent' })}
                        className="h-11"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-last-name" className="text-slate-700">Last Name *</Label>
                      <Input
                        id="signup-last-name"
                        data-testid="signup-last-name"
                        type="text"
                        placeholder="Doe"
                        value={registerData.last_name}
                        onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value, role: 'parent' })}
                        className="h-11"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signup-dob" className="text-slate-700">Date of Birth *</Label>
                    <Input
                      id="signup-dob"
                      data-testid="signup-dob"
                      type="date"
                      value={registerData.date_of_birth}
                      onChange={(e) => setRegisterData({ ...registerData, date_of_birth: e.target.value, role: 'parent' })}
                      className="h-11"
                      required
                      max={new Date().toISOString().split('T')[0]}
                    />
                    <p className="text-xs text-slate-500">Must be 18 years or older</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email" className="text-slate-700">Email Address *</Label>
                    <Input
                      id="signup-email"
                      data-testid="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value, role: 'parent' })}
                      className="h-11"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-phone" className="text-slate-700">Phone Number *</Label>
                    <Input
                      id="signup-phone"
                      data-testid="signup-phone"
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value, role: 'parent' })}
                      className="h-11"
                      required
                    />
                    <p className="text-xs text-slate-500">Include country code (e.g., +1 for US)</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-password" className="text-slate-700">Password *</Label>
                    <Input
                      id="signup-password"
                      data-testid="signup-password"
                      type="password"
                      placeholder="Minimum 8 characters"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value, role: 'parent' })}
                      className="h-11"
                      required
                    />
                    {registerData.password && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                        <p className="text-xs font-semibold text-slate-600 mb-2">Password must contain:</p>
                        <PasswordRequirement met={passwordRequirements.minLength} text="At least 8 characters" />
                        <PasswordRequirement met={passwordRequirements.hasUppercase} text="One uppercase letter (A-Z)" />
                        <PasswordRequirement met={passwordRequirements.hasLowercase} text="One lowercase letter (a-z)" />
                        <PasswordRequirement met={passwordRequirements.hasNumber} text="One number (0-9)" />
                        <PasswordRequirement met={passwordRequirements.hasSpecial} text="One special character (!@#$%^&*)" />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm" className="text-slate-700">Confirm Password *</Label>
                    <Input
                      id="signup-confirm"
                      data-testid="signup-confirm-password"
                      type="password"
                      placeholder="Re-enter password"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value, role: 'parent' })}
                      className="h-11"
                      required
                    />
                  </div>

                  <div className="pt-4">
                    <button
                      data-testid="signup-button"
                      type="submit"
                      disabled={loading}
                      className="w-full h-14 px-4 py-3 text-lg font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                          <span>Creating account...</span>
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-5 h-5" />
                          <span>Create Parent Account</span>
                        </>
                      )}
                    </button>
                  </div>

                  <p className="text-center text-sm text-slate-500 mt-4">
                    Are you a professional? <button type="button" onClick={() => document.querySelector('[data-testid="register-tab"]')?.click()} className="text-indigo-600 hover:text-indigo-700 font-medium">Register as Attorney, Provider, or Court Rep</button>
                  </p>
                </form>
                )}
              </TabsContent>

              <TabsContent value="register" className="mt-6">
                <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-sm text-indigo-800">
                    <strong>Professional Registration:</strong> For attorneys, providers, and court representatives. Parents should use the "Sign Up" tab.
                  </p>
                </div>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="register-first-name" className="text-slate-700">First Name *</Label>
                      <Input
                        id="register-first-name"
                        data-testid="register-first-name"
                        type="text"
                        placeholder="John"
                        value={registerData.first_name}
                        onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                        className="h-11"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-last-name" className="text-slate-700">Last Name *</Label>
                      <Input
                        id="register-last-name"
                        data-testid="register-last-name"
                        type="text"
                        placeholder="Doe"
                        value={registerData.last_name}
                        onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                        className="h-11"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-dob" className="text-slate-700">Date of Birth *</Label>
                    <Input
                      id="register-dob"
                      data-testid="register-dob"
                      type="date"
                      value={registerData.date_of_birth}
                      onChange={(e) => setRegisterData({ ...registerData, date_of_birth: e.target.value })}
                      className="h-11"
                      required
                      max={new Date().toISOString().split('T')[0]}
                    />
                    <p className="text-xs text-slate-500">Must be 18 years or older</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-slate-700">Email Address *</Label>
                    <Input
                      id="register-email"
                      data-testid="register-email"
                      type="email"
                      placeholder="you@example.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      className="h-11"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-role" className="text-slate-700">Professional Type</Label>
                    <select
                      id="register-role"
                      value={registerData.role === 'parent' ? 'attorney' : registerData.role}
                      onChange={(e) => setRegisterData({ ...registerData, role: e.target.value })}
                      className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      required
                    >
                      <option value="attorney">Attorney</option>
                      <option value="provider">Provider (Therapist, Mediator, etc.)</option>
                      <option value="court_rep">Court Representative</option>
                    </select>
                    <p className="text-sm text-slate-500">
                      {registerData.role === 'attorney' && 'Attorney managing family law cases'}
                      {registerData.role === 'provider' && 'Mental health or family services provider'}
                      {registerData.role === 'court_rep' && 'Court official or guardian ad litem'}
                    </p>
                  </div>

                  {/* Attorney-specific fields */}
                  {registerData.role === 'attorney' && (
                    <>
                      <div className="space-y-2 bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                        <h3 className="font-semibold text-indigo-900 mb-3">Attorney License Information</h3>
                        <p className="text-xs text-indigo-700 mb-3 p-2 bg-indigo-100 rounded">
                          ⚖️ Nevada Bar licenses are automatically verified. Other states require manual verification.
                        </p>
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="bar-license" className="text-slate-700">Bar License Number *</Label>
                            <Input
                              id="bar-license"
                              type="text"
                              placeholder="e.g., Bar#13126 or 13126"
                              value={registerData.bar_license_number}
                              onChange={(e) => setRegisterData({ ...registerData, bar_license_number: e.target.value })}
                              required
                            />
                            <p className="text-xs text-slate-500 mt-1">Enter your bar number (format: Bar#13126 or just 13126)</p>
                          </div>
                          <div>
                            <Label htmlFor="license-state" className="text-slate-700">License State *</Label>
                            <select
                              id="license-state"
                              value={registerData.license_state}
                              onChange={(e) => setRegisterData({ ...registerData, license_state: e.target.value })}
                              className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              required
                            >
                              <option value="">Select state...</option>
                              <option value="Nevada">Nevada (Auto-verified)</option>
                              <option value="California">California</option>
                              <option value="Arizona">Arizona</option>
                              <option value="Utah">Utah</option>
                              <option value="Other">Other State</option>
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="license-expiration" className="text-slate-700">License Expiration Date *</Label>
                            <Input
                              id="license-expiration"
                              type="date"
                              value={registerData.license_expiration_date}
                              onChange={(e) => setRegisterData({ ...registerData, license_expiration_date: e.target.value })}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="law-firm" className="text-slate-700">Law Firm Name (Optional)</Label>
                            <Input
                              id="law-firm"
                              type="text"
                              placeholder="e.g., Smith & Associates"
                              value={registerData.law_firm_name}
                              onChange={(e) => setRegisterData({ ...registerData, law_firm_name: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Provider-specific fields */}
                  {registerData.role === 'provider' && (
                    <>
                      <div className="space-y-2 bg-green-50 p-4 rounded-lg border border-green-200">
                        <h3 className="font-semibold text-green-900 mb-3">Provider Information</h3>
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="provider-type" className="text-slate-700">Provider Type *</Label>
                            <select
                              id="provider-type"
                              value={registerData.provider_type}
                              onChange={(e) => setRegisterData({ ...registerData, provider_type: e.target.value })}
                              className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              required
                            >
                              <option value="">Select type...</option>
                              <option value="therapist">Therapist</option>
                              <option value="mediator">Mediator</option>
                              <option value="counselor">Counselor</option>
                              <option value="evaluator">Custody Evaluator</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="provider-license" className="text-slate-700">License Number (Optional)</Label>
                            <Input
                              id="provider-license"
                              type="text"
                              placeholder="Professional license number"
                              value={registerData.provider_license_number}
                              onChange={(e) => setRegisterData({ ...registerData, provider_license_number: e.target.value })}
                            />
                          </div>
                          <div>
                            <Label htmlFor="provider-state" className="text-slate-700">License State (Optional)</Label>
                            <Input
                              id="provider-state"
                              type="text"
                              placeholder="e.g., California"
                              value={registerData.provider_license_state}
                              onChange={(e) => setRegisterData({ ...registerData, provider_license_state: e.target.value })}
                            />
                          </div>
                          <div>
                            <Label htmlFor="organization" className="text-slate-700">Organization Name (Optional)</Label>
                            <Input
                              id="organization"
                              type="text"
                              placeholder="e.g., Family Wellness Center"
                              value={registerData.organization_name}
                              onChange={(e) => setRegisterData({ ...registerData, organization_name: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Court Representative-specific fields */}
                  {registerData.role === 'court_rep' && (
                    <>
                      <div className="space-y-2 bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <h3 className="font-semibold text-purple-900 mb-3">Court Representative Information</h3>
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="court-name" className="text-slate-700">Court Name *</Label>
                            <Input
                              id="court-name"
                              type="text"
                              placeholder="e.g., Superior Court of California"
                              value={registerData.court_name}
                              onChange={(e) => setRegisterData({ ...registerData, court_name: e.target.value })}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="jurisdiction" className="text-slate-700">Jurisdiction *</Label>
                            <Input
                              id="jurisdiction"
                              type="text"
                              placeholder="e.g., Los Angeles County"
                              value={registerData.jurisdiction}
                              onChange={(e) => setRegisterData({ ...registerData, jurisdiction: e.target.value })}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="position" className="text-slate-700">Position *</Label>
                            <select
                              id="position"
                              value={registerData.position}
                              onChange={(e) => setRegisterData({ ...registerData, position: e.target.value })}
                              className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              required
                            >
                              <option value="">Select position...</option>
                              <option value="judge">Judge</option>
                              <option value="magistrate">Magistrate</option>
                              <option value="court_clerk">Court Clerk</option>
                              <option value="guardian_ad_litem">Guardian ad Litem</option>
                              <option value="case_manager">Case Manager</option>
                              <option value="court_appointed_special_advocate">Court Appointed Special Advocate (CASA)</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="register-phone" className="text-slate-700">Phone Number *</Label>
                    <Input
                      id="register-phone"
                      data-testid="register-phone"
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                      className="h-11"
                      required
                    />
                    <p className="text-xs text-slate-500">Include country code (e.g., +1 for US)</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-slate-700">Password</Label>
                    <Input
                      id="register-password"
                      data-testid="register-password"
                      type="password"
                      placeholder="Minimum 8 characters"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      className="h-11"
                      required
                    />
                    {registerData.password && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                        <p className="text-xs font-semibold text-slate-600 mb-2">Password must contain:</p>
                        <PasswordRequirement met={passwordRequirements.minLength} text="At least 8 characters" />
                        <PasswordRequirement met={passwordRequirements.hasUppercase} text="One uppercase letter (A-Z)" />
                        <PasswordRequirement met={passwordRequirements.hasLowercase} text="One lowercase letter (a-z)" />
                        <PasswordRequirement met={passwordRequirements.hasNumber} text="One number (0-9)" />
                        <PasswordRequirement met={passwordRequirements.hasSpecial} text="One special character (!@#$%^&*)" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm" className="text-slate-700">Confirm Password</Label>
                    <Input
                      id="register-confirm"
                      data-testid="register-confirm-password"
                      type="password"
                      placeholder="Re-enter password"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                      className="h-11"
                      required
                    />
                  </div>
                  <div className="pt-6">
                    <button
                      data-testid="register-button"
                      type="submit"
                      disabled={loading}
                      className="w-full h-14 px-4 py-3 text-lg font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                          <span>Creating account...</span>
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-5 h-5" />
                          <span>Create Account</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
